import Checkbox from "@mui/material/Checkbox";
import LinearProgress from "@mui/material/LinearProgress";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useSignUpWithCredentials } from "0.Mine/1.Hooks/security/signup";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";

// Images
const bgImage = "https://qlickup.it/media/ai.jpg";

function Illustration() {
  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreement, setAgreement] = useState(true);

  // Validation states
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [emailValid, setEmailValid] = useState(true);
  const { t } = useTranslation();
  const handleSetAgreement = () => setAgreement(!agreement);
  const { signupWithCredentials } = useSignUpWithCredentials();

  // Check password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordFeedback("");
      return;
    }

    // Password strength criteria
    let strength = 0;
    let feedback = [];

    // Length check (at least 8 characters)
    if (password.length >= 8) {
      strength += 25;
    } else {
      feedback.push("Use at least 8 characters");
    }

    // Contains numbers
    if (/\d/.test(password)) {
      strength += 25;
    } else {
      feedback.push("Add numbers");
    }

    // Contains lowercase letters
    if (/[a-z]/.test(password)) {
      strength += 25;
    } else {
      feedback.push("Add lowercase letters");
    }

    // Contains uppercase letters or special characters
    if (/[A-Z]/.test(password) || /[^a-zA-Z0-9]/.test(password)) {
      strength += 25;
    } else {
      feedback.push("Add uppercase letters or symbols");
    }

    setPasswordStrength(strength);
    setPasswordFeedback(feedback.join(", "));

    // Check if passwords match whenever password changes
    if (confirmPassword) {
      setPasswordsMatch(password === confirmPassword);
    }
  }, [password, confirmPassword]);

  // Check password match
  useEffect(() => {
    if (confirmPassword) {
      setPasswordsMatch(password === confirmPassword);
    }
  }, [confirmPassword, password]);

  // Validate email format
  useEffect(() => {
    if (!email) {
      setEmailValid(true);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailValid(emailRegex.test(email));
  }, [email]);

  // Get password strength color
  const getPasswordStrengthColor = () => {
    if (passwordStrength < 50) return "error";
    if (passwordStrength < 75) return "warning";
    return "success";
  };

  // Handle sign up
  const handleSignUp = async () => {
    // Log form data
    console.log({
      name,
      email,
      password,
      confirmPassword,
      agreement,
    });

    // Validate form before submission
    if (!name || !email || !password || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      return;
    }

    if (passwordStrength < 50) {
      return;
    }

    if (!emailValid) {
      return;
    }

    if (!agreement) {
      return;
    }

    await signupWithCredentials(name, email, password, confirmPassword);
  };

  return (
    <IllustrationLayout
      title={t("signup.signup")}
      description={t("signin.enterEmail")}
      illustration={{
        image: bgImage,
        title: "",
        description: "",
      }}
    >
      <ArgonBox component="form" role="form">
        <ArgonBox mb={2}>
          <ArgonInput
            placeholder="Name"
            size="large"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </ArgonBox>
        <ArgonBox mb={2}>
          <ArgonInput
            type="email"
            placeholder="Email"
            size="large"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!emailValid && email.length > 0}
          />
          {!emailValid && email.length > 0 && (
            <ArgonTypography variant="caption" color="error" fontWeight="light">
              Please enter a valid email address
            </ArgonTypography>
          )}
        </ArgonBox>
        <ArgonBox mb={2}>
          <ArgonInput
            type="password"
            placeholder="Password"
            size="large"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {password && (
            <>
              <ArgonBox mt={0.5}>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  color={getPasswordStrengthColor()}
                  sx={{ height: 4, borderRadius: 2 }}
                />
              </ArgonBox>
              <ArgonTypography
                variant="caption"
                color={passwordStrength >= 75 ? "success" : "text"}
                fontWeight="light"
                sx={{ display: "block", mt: 0.5 }}
              >
                {passwordStrength < 50
                  ? "Weak password"
                  : passwordStrength < 75
                  ? "Medium password"
                  : "Strong password"}
                {passwordFeedback && ` - ${passwordFeedback}`}
              </ArgonTypography>
            </>
          )}
        </ArgonBox>
        <ArgonBox mb={2}>
          <ArgonInput
            type="password"
            placeholder="Confirm Password"
            size="large"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!passwordsMatch && confirmPassword.length > 0}
          />
          {!passwordsMatch && confirmPassword.length > 0 && (
            <ArgonTypography variant="caption" color="error" fontWeight="light">
              Passwords do not match
            </ArgonTypography>
          )}
        </ArgonBox>
        <ArgonBox display="flex" alignItems="center">
          <Checkbox checked={agreement} onChange={handleSetAgreement} />
          <ArgonTypography
            variant="button"
            fontWeight="regular"
            onClick={handleSetAgreement}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            &nbsp;&nbsp;I agree the&nbsp;
          </ArgonTypography>
          <ArgonTypography component="a" href="#" variant="button" fontWeight="bold" textGradient>
            Terms and Conditions
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox mt={4} mb={1}>
          <ArgonButton
            color="info"
            size="large"
            fullWidth
            onClick={handleSignUp}
            disabled={
              !name ||
              !email ||
              !password ||
              !confirmPassword ||
              !passwordsMatch ||
              passwordStrength < 50 ||
              !emailValid ||
              !agreement
            }
          >
            {t("signup.signupButton")}
          </ArgonButton>
        </ArgonBox>
        <ArgonBox mt={3} textAlign="center">
          <ArgonTypography variant="button" color="text" fontWeight="regular">
            Already have an account?&nbsp;
            <ArgonTypography
              component={Link}
              to="/signin"
              variant="button"
              color="info"
              fontWeight="bold"
            >
              {t("signin.signin")}
            </ArgonTypography>
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>
    </IllustrationLayout>
  );
}

export default Illustration;
